import { Download, Share2, PlusSquare, Check, X, Smartphone, MoreVertical, Apple } from 'lucide-react'
import { useState } from 'react'
import { usePWAInstall } from '@/hooks/usePWAInstall'
import { CoreController } from '@/services/coreController'

type Platform = 'ios' | 'android' | 'desktop' | 'unknown'
type GuideTab = 'ios' | 'android'

const IOS_STEPS = [
  {
    step: '1',
    title: 'Ouvrez Safari',
    desc: 'Utilisez le navigateur Safari sur votre iPhone.',
    Icon: Smartphone,
  },
  {
    step: '2',
    title: 'Appuyez sur Partager',
    desc: "Touchez l'icône Partage au centre/bas de l'écran.",
    Icon: Share2,
  },
  {
    step: '3',
    title: "Sur l'écran d'accueil",
    desc: "Faites défiler et touchez 'Sur l'écran d'accueil'.",
    Icon: PlusSquare,
  },
  {
    step: '4',
    title: 'Ajouter',
    desc: "Confirmez en touchant 'Ajouter' en haut à droite.",
    Icon: Check,
  },
]

const ANDROID_STEPS = [
  {
    step: '1',
    title: 'Ouvrez Chrome',
    desc: "Utilisez le navigateur Chrome sur votre téléphone Android.",
    Icon: Smartphone,
  },
  {
    step: '2',
    title: 'Ouvrez le menu',
    desc: "Touchez les trois points (⋮) en haut à droite de l'écran.",
    Icon: MoreVertical,
  },
  {
    step: '3',
    title: 'Installer l’application',
    desc: "Sélectionnez « Installer l'application » ou « Ajouter à l'écran d'accueil ».",
    Icon: PlusSquare,
  },
  {
    step: '4',
    title: 'Confirmer',
    desc: "Touchez « Installer » pour finaliser l'ajout à votre écran d'accueil.",
    Icon: Check,
  },
]

export function DownloadAppButton() {
  const { platform, canInstall, showIosGuide, showAndroidGuide } = usePWAInstall()
  const [showGuide, setShowGuide] = useState(false)
  const [activeTab, setActiveTab] = useState<GuideTab>('ios')

  if (!canInstall) return null

  const appName = CoreController.instance.appSettingResponse.config?.APP_NAME ?? 'EasyService'

  const handleClick = () => {
    const initialTab: GuideTab = platform === 'android' ? 'android' : 'ios'
    setActiveTab(initialTab)
    if (initialTab === 'ios') showIosGuide()
    else showAndroidGuide()
    setShowGuide(true)
  }

  const handleTabChange = (tab: GuideTab) => {
    setActiveTab(tab)
    if (tab === 'ios') showIosGuide()
    else showAndroidGuide()
  }

  const steps = activeTab === 'ios' ? IOS_STEPS : ANDROID_STEPS

  return (
    <>
      <button
        onClick={handleClick}
        className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full font-semibold shadow-lg transition-all text-sm"
        aria-label="Télécharger l'application"
      >
        <Download className="w-4 h-4" />
        <span className="hidden sm:inline">Télécharger l'app</span>
        <span className="sm:hidden">App</span>
      </button>

      {showGuide && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4">
          <div className="bg-white w-full sm:w-[420px] sm:rounded-3xl rounded-t-3xl shadow-2xl p-6 animate-in slide-in-from-bottom-8 duration-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-gray-900">Installer l'application</h3>
              <button
                onClick={() => setShowGuide(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Fermer"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-5">
              Ajoutez {appName} à votre écran d'accueil pour un accès rapide.
            </p>

            <div
              role="tablist"
              aria-label="Instructions d'installation"
              className="flex bg-gray-100 rounded-2xl p-1 mb-5"
            >
              {(
                [
                  { key: 'ios' as const, label: 'iPhone', Icon: Apple },
                  { key: 'android' as const, label: 'Android', Icon: Smartphone },
                ]
              ).map(({ key, label, Icon }) => {
                const isActive = activeTab === key
                return (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => handleTabChange(key)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl text-sm font-semibold transition-all ${
                      isActive
                        ? 'bg-white text-blue-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                )
              })}
            </div>

            <div className="space-y-4" role="tabpanel">
              {steps.map(({ step, title, desc, Icon }) => (
                <div key={`${activeTab}-${step}`} className="flex items-start gap-4 bg-gray-50 rounded-2xl p-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                    {step}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className="w-4 h-4 text-gray-500" />
                      <h4 className="font-semibold text-gray-900">{title}</h4>
                    </div>
                    <p className="text-sm text-gray-600 leading-snug">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowGuide(false)}
              className="w-full mt-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors"
            >
              J'ai compris
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default DownloadAppButton
